import { useEffect, useMemo, useState } from 'react';
import Modal from '../../ui/Modal/Modal';
import Input from '../../ui/Input/Input';
import Button from '../../ui/Button/Button';
import styles from './CharacterPickerModal.module.css';

// Free-text character name picker with autocomplete from previously-used
// names in the codex. Multi-word names allowed (e.g. "JOAO GRILO"). Always
// uppercased on submit. Returns the chosen name via `onSubmit(name)`.
export default function CharacterPickerModal({
  isOpen,
  onClose,
  onSubmit,
  known = [],
  title = 'Insert character',
  submitLabel = 'Insert',
}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setValue('');
    setError(null);
  }, [isOpen]);

  const suggestions = useMemo(() => {
    const q = value.trim().toUpperCase();
    if (!q) return known.slice(0, 8);
    return known.filter((n) => n.includes(q)).slice(0, 8);
  }, [value, known]);

  const submit = (name) => {
    const v = (name ?? value).trim().toUpperCase();
    if (!v) {
      setError('Name is required');
      return;
    }
    onSubmit(v);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => submit()}>{submitLabel}</Button>
        </>
      }
    >
      <Input
        label="Name"
        placeholder="e.g. JOAO GRILO"
        value={value}
        onChange={(e) => { setValue(e.target.value); setError(null); }}
        onKeyDown={onKeyDown}
        error={error}
        autoFocus
      />
      {suggestions.length > 0 && (
        <ul className={styles.list}>
          {suggestions.map((n) => (
            <li
              key={n}
              className={styles.row}
              onClick={() => submit(n)}
            >
              <span>{n}</span>
              <span className={styles.rowMeta}>known</span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
